import { useState } from 'react';
import { Card, KpiCard, Badge, Modal, SlidePanel, toast } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import styles from './MpListings.module.css';

interface Listing {
  id: string;
  property_id: string;
  notification_no: string;
  license_type: string;
  max_guests: number;
  airbnb_status: string;
  booking_status: string;
  stay_status?: string;
  days_used: number;
  max_days: number;
  status: string;
  property_name?: string;
  address?: string;
  notification_date?: string;
  smart_lock_code?: string;
  wifi_ssid?: string;
  wifi_password?: string;
  checkin_instructions?: string;
  ical_url_airbnb?: string;
  ical_url_booking?: string;
  ical_url_stay?: string;
  ical_last_synced_at?: string;
}

interface Property {
  id: string;
  name: string;
  address: string;
}

interface RevenueData {
  kpis: { totalRevenue: number; totalBookings: number; avgPerNight: number; occupancy: string };
}

interface ListingRevenue {
  listing_id: string;
  revenue: number;
  bookings: number;
  nights: number;
}

const statusLabel: Record<string, string> = {
  active: '営業中', cleaning: '清掃中', suspended: '停止中', preparing: '準備中',
};
const statusVariant: Record<string, 'green' | 'amber' | 'red' | 'gray'> = {
  active: 'green', cleaning: 'amber', suspended: 'red', preparing: 'gray',
};

export function MpListings() {
  const { data: listings, loading, refetch } = useApi<Listing[]>('/tenant/minpaku/listings');
  const { data: revenueData } = useApi<RevenueData>('/tenant/minpaku/revenue');
  const { data: listingRevenue } = useApi<ListingRevenue[]>('/tenant/minpaku/revenue/by-listing');
  const { data: properties } = useApi<Property[]>('/tenant/properties');
  const [otaFilter, setOtaFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCount, setShowCount] = useState(6);
  const [showCreate, setShowCreate] = useState(false);
  const [showTodokede, setShowTodokede] = useState(false);
  const [icalTarget, setIcalTarget] = useState<Listing | null>(null);
  const [icalForm, setIcalForm] = useState({ airbnb: '', booking: '', stay: '' });
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    property_id: '', notification_no: '', license_type: 'housing_business',
    max_guests: '2', max_days: '180', status: 'preparing',
    notification_date: '', smart_lock_code: '', wifi_ssid: '', wifi_password: '',
  });

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  const allListings = listings ?? [];
  const filtered = allListings.filter((l) => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (otaFilter === 'airbnb' && l.airbnb_status !== 'connected') return false;
    if (otaFilter === 'booking' && l.booking_status !== 'connected') return false;
    return true;
  });

  const totalProperties = allListings.length;
  const avgOccupancy = totalProperties > 0
    ? (allListings.reduce((sum, l) => sum + Math.min((l.days_used / (l.max_days || 180)) * 100, 100), 0) / totalProperties).toFixed(1)
    : '0.0';
  const totalRevenue = revenueData?.kpis.totalRevenue ?? 0;
  const alertCount = allListings.filter((l) => (l.days_used / (l.max_days || 180)) > 0.85).length;

  const kpis = [
    { label: '管理民泊物件数', value: `${totalProperties}件`, delta: '', trend: 'flat' as const },
    { label: '今月稼働率（平均）', value: `${avgOccupancy}%`, delta: '', trend: 'up' as const },
    { label: '今月OTA売上合計', value: `¥${totalRevenue.toLocaleString()}`, delta: '', trend: 'up' as const },
    { label: '180日上限アラート', value: `${alertCount}件`, delta: alertCount > 0 ? '要確認' : '', trend: alertCount > 0 ? 'down' as const : 'flat' as const },
  ];

  // Properties not yet registered as minpaku
  const registeredPropertyIds = new Set(allListings.map(l => l.property_id));
  const availableProperties = (properties ?? []).filter(p => !registeredPropertyIds.has(p.id));

  const handleCreate = async () => {
    if (!form.property_id) { toast('物件を選択してください', 'error'); return; }
    if (!form.notification_no) { toast('届出番号を入力してください', 'error'); return; }
    try {
      const body: Record<string, unknown> = {
        property_id: form.property_id, notification_no: form.notification_no,
        license_type: form.license_type, max_guests: Number(form.max_guests),
        max_days: Number(form.max_days), status: form.status,
      };
      if (form.notification_date) body.notification_date = form.notification_date;
      if (form.smart_lock_code) body.smart_lock_code = form.smart_lock_code;
      if (form.wifi_ssid) body.wifi_ssid = form.wifi_ssid;
      if (form.wifi_password) body.wifi_password = form.wifi_password;
      await api('/tenant/minpaku/listings', { method: 'POST', body: JSON.stringify(body) });
      toast('民泊届出を登録しました', 'success');
      setShowCreate(false);
      setForm({ property_id: '', notification_no: '', license_type: 'housing_business', max_guests: '2', max_days: '180', status: 'preparing', notification_date: '', smart_lock_code: '', wifi_ssid: '', wifi_password: '' });
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました', 'error');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <div className={styles.toolbar}>
        <select className={styles.filterSelect} value={otaFilter} onChange={(e) => setOtaFilter(e.target.value)}>
          <option value="all">全OTA</option>
          <option value="airbnb">Airbnb</option>
          <option value="booking">Booking.com</option>
        </select>
        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">全ステータス</option>
          <option value="active">営業中</option><option value="cleaning">清掃中</option>
          <option value="suspended">停止中</option><option value="preparing">準備中</option>
        </select>
        <span className={styles.spacer} />
        <button className={styles.btn} onClick={() => setShowTodokede(true)}>📋 届出一覧</button>
        <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>＋ 民泊届出登録</button>
      </div>

      <div className={styles.cardGrid}>
        {filtered.slice(0, showCount).map((l) => {
          const maxDays = l.max_days || 180;
          const occupancy = Math.min((l.days_used / maxDays) * 100, 100);
          const circumference = 2 * Math.PI * 22;
          const offset = circumference - (occupancy / 100) * circumference;
          const gaugeColor = occupancy > 85 ? 'var(--red)' : occupancy > 60 ? 'var(--amber)' : 'var(--green)';
          const rev = (listingRevenue ?? []).find(r => r.listing_id === l.id);
          const monthRevenue = rev?.revenue ?? 0;
          const avgPrice = rev && rev.nights > 0 ? Math.round(rev.revenue / rev.nights) : 0;

          return (
            <div key={l.id} className={styles.propCard}>
              <div className={styles.propCardHeader}>
                <Badge variant={statusVariant[l.status] ?? 'gray'}>{statusLabel[l.status] ?? l.status}</Badge>
                <span className={styles.propIcon}>🏠</span>
                <div style={{ flex: 1 }}>
                  <div className={styles.propName}>{l.property_name ?? '—'}</div>
                  <div className={styles.propAddress}>{l.address ?? ''}</div>
                </div>
              </div>
              <div className={styles.gaugeRow}>
                <div className={styles.gauge}>
                  <svg className={styles.gaugeSvg} viewBox="0 0 50 50">
                    <circle className={styles.gaugeTrack} cx="25" cy="25" r="22" />
                    <circle className={styles.gaugeFill} cx="25" cy="25" r="22" stroke={gaugeColor} strokeDasharray={circumference} strokeDashoffset={offset} />
                  </svg>
                  <span className={styles.gaugeText}>{l.days_used}/{maxDays}</span>
                </div>
                <div className={styles.propStats}>
                  <div className={styles.statRow}><span className={styles.statLabel}>今月売上</span><span className={styles.statValue} style={{ color: 'var(--brand)', fontFamily: 'var(--mono)' }}>¥{monthRevenue.toLocaleString()}</span></div>
                  <div className={styles.statRow}><span className={styles.statLabel}>稼働率</span><span className={styles.statValue}>{occupancy.toFixed(1)}%</span></div>
                  <div className={styles.statRow}><span className={styles.statLabel}>平均単価</span><span className={styles.statValue} style={{ fontFamily: 'var(--mono)' }}>{avgPrice > 0 ? `¥${avgPrice.toLocaleString()}` : '—'}</span></div>
                </div>
              </div>
              <div className={styles.otaRow}>
                <span className={`${styles.otaBadge} ${l.ical_url_airbnb || l.airbnb_status === 'connected' ? styles.connected : styles.none}`}>Airbnb {l.ical_url_airbnb || l.airbnb_status === 'connected' ? '✓' : '—'}</span>
                <span className={`${styles.otaBadge} ${l.ical_url_stay || (l.stay_status ?? 'none') === 'connected' ? styles.connected : styles.none}`}>STAY {l.ical_url_stay || (l.stay_status ?? 'none') === 'connected' ? '✓' : '—'}</span>
                <span className={`${styles.otaBadge} ${l.ical_url_booking || l.booking_status === 'connected' ? styles.connected : styles.none}`}>Booking {l.ical_url_booking || l.booking_status === 'connected' ? '✓' : '—'}</span>
                <button className={styles.icalBtn} onClick={(e) => { e.stopPropagation(); setIcalTarget(l); setIcalForm({ airbnb: l.ical_url_airbnb || '', booking: l.ical_url_booking || '', stay: l.ical_url_stay || '' }); }}>🔗 iCal</button>
              </div>
              {l.ical_last_synced_at && <div className={styles.syncTime}>最終同期: {new Date(l.ical_last_synced_at).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)', gridColumn: '1 / -1' }}>該当する物件がありません</div>
        )}
        {filtered.length > showCount && (
          <div className={styles.showMoreCard} onClick={() => setShowCount(showCount + 6)}>
            残り{filtered.length - showCount}件を表示
          </div>
        )}
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="民泊届出登録" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btn} onClick={() => setShowCreate(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleCreate}>登録</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>対象物件 *</label>
            <select className={styles.formInput} value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })}>
              <option value="">選択してください</option>
              {availableProperties.map(p => <option key={p.id} value={p.id}>{p.name}（{p.address}）</option>)}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>届出番号 *</label>
            <input className={styles.formInput} value={form.notification_no} onChange={e => setForm({ ...form, notification_no: e.target.value })} placeholder="M131234567" />
            <span className={styles.formHint}>住宅宿泊事業届出番号（例: M131234567）</span>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>免許種別</label>
              <select className={styles.formInput} value={form.license_type} onChange={e => setForm({ ...form, license_type: e.target.value })}>
                <option value="housing_business">住宅宿泊事業（180日制限）</option>
                <option value="special_zone">特区民泊（365日可）</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>定員</label>
              <input className={styles.formInput} type="number" value={form.max_guests} onChange={e => setForm({ ...form, max_guests: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>届出日</label>
            <input className={styles.formInput} type="date" value={form.notification_date} onChange={e => setForm({ ...form, notification_date: e.target.value })} />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>スマートロックコード</label>
              <input className={styles.formInput} value={form.smart_lock_code} onChange={e => setForm({ ...form, smart_lock_code: e.target.value })} placeholder="1234#" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>WiFi SSID</label>
              <input className={styles.formInput} value={form.wifi_ssid} onChange={e => setForm({ ...form, wifi_ssid: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>WiFi パスワード</label>
            <input className={styles.formInput} value={form.wifi_password} onChange={e => setForm({ ...form, wifi_password: e.target.value })} />
          </div>
        </div>
      </Modal>

      {/* 届出一覧 SlidePanel */}
      <SlidePanel open={showTodokede} onClose={() => setShowTodokede(false)} title="📋 届出一覧" width={560}>
        <PanelSection title={`登録済み届出（${allListings.length}件）`}>
          {allListings.map(l => {
            const maxDays = l.max_days || 180;
            const ratio = l.days_used / maxDays;
            const alertColor = ratio > 0.9 ? 'var(--red)' : ratio > 0.75 ? 'var(--amber)' : 'var(--green)';
            return (
              <div key={l.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{l.property_name ?? '—'}</span>
                  <Badge variant={statusVariant[l.status] ?? 'gray'}>{statusLabel[l.status] ?? l.status}</Badge>
                </div>
                <SpecGrid>
                  <SpecCell label="届出番号" value={l.notification_no} mono />
                  <SpecCell label="届出日" value={l.notification_date ? new Date(l.notification_date).toLocaleDateString('ja-JP') : '—'} />
                  <SpecCell label="種別" value={l.license_type === 'housing_business' ? '住宅宿泊事業' : '特区民泊'} />
                  <SpecCell label="定員" value={`${l.max_guests}名`} />
                </SpecGrid>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>営業日数</span>
                  <div style={{ flex: 1, height: 6, background: 'var(--surface3)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(ratio * 100, 100)}%`, height: '100%', background: alertColor, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 12, fontFamily: 'var(--mono)', fontWeight: 700, color: alertColor }}>{l.days_used}/{maxDays}日</span>
                </div>
              </div>
            );
          })}
          {allListings.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink3)' }}>届出なし</div>}
        </PanelSection>
      </SlidePanel>

      {/* iCal設定 SlidePanel */}
      <SlidePanel open={!!icalTarget} onClose={() => setIcalTarget(null)} title={`🔗 iCal連携 — ${icalTarget?.property_name ?? ''}`} width={520}
        actions={<button className={styles.btnPrimary} onClick={async () => {
          if (!icalTarget) return;
          try {
            await api(`/tenant/minpaku/listings/${icalTarget.id}`, { method: 'PATCH', body: JSON.stringify({
              ical_url_airbnb: icalForm.airbnb || null, ical_url_booking: icalForm.booking || null, ical_url_stay: icalForm.stay || null,
            })});
            toast('iCal URLを保存しました', 'success'); refetch();
          } catch { toast('保存に失敗しました', 'error'); }
        }}>保存</button>}>
        {icalTarget && <>
          <PanelSection title="iCal フィード URL">
            <div className={styles.icalForm}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Airbnb iCal URL</label>
                <input className={styles.icalInput} value={icalForm.airbnb} onChange={e => setIcalForm({ ...icalForm, airbnb: e.target.value })} placeholder="https://www.airbnb.com/calendar/ical/XXXXX.ics" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Booking.com iCal URL</label>
                <input className={styles.icalInput} value={icalForm.booking} onChange={e => setIcalForm({ ...icalForm, booking: e.target.value })} placeholder="https://admin.booking.com/xxx/ical.ics" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>STAY JAPAN iCal URL</label>
                <input className={styles.icalInput} value={icalForm.stay} onChange={e => setIcalForm({ ...icalForm, stay: e.target.value })} placeholder="https://stayjapan.com/calendar/xxx.ics" />
              </div>
            </div>
          </PanelSection>
          <PanelSection title="同期">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className={styles.btnPrimary} disabled={syncing} onClick={async () => {
                setSyncing(true);
                try {
                  const res = await api<{ synced: number; skipped: number; errors: string[] }>(`/tenant/minpaku/listings/${icalTarget.id}/ical-sync`, { method: 'POST' });
                  toast(`同期完了: ${res.synced}件取得${res.skipped > 0 ? ` / ${res.skipped}件スキップ` : ''}${res.errors.length > 0 ? ` / エラー${res.errors.length}件` : ''}`, res.errors.length > 0 ? 'error' : 'success');
                  refetch();
                } catch (e) { toast(e instanceof Error ? e.message : '同期に失敗しました', 'error'); }
                setSyncing(false);
              }}>{syncing ? '同期中...' : '🔄 同期実行'}</button>
              <div style={{ fontSize: 12, color: 'var(--ink3)' }}>
                {icalTarget.ical_last_synced_at
                  ? `最終同期: ${new Date(icalTarget.ical_last_synced_at).toLocaleString('ja-JP')}`
                  : '未同期'}
              </div>
            </div>
          </PanelSection>
          <PanelSection title="iCal URLの取得方法">
            <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.7 }}>
              <div style={{ marginBottom: 8 }}><strong>Airbnb:</strong> ホスト管理画面 → カレンダー → カレンダーをエクスポート → URLをコピー</div>
              <div style={{ marginBottom: 8 }}><strong>Booking.com:</strong> 管理画面 → 料金・空室状況 → 同期 → iCalリンクをコピー</div>
              <div><strong>STAY JAPAN:</strong> 管理画面 → カレンダー連携 → エクスポートURL</div>
            </div>
          </PanelSection>
        </>}
      </SlidePanel>
    </div>
  );
}
